import { useCallback, useState } from 'react';
import type { Result } from '@api';
import { Failure, Success, useErrorMessage, useDatahubAPI } from '@api';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useTranslation } from 'react-i18next';
import type { EmergencyTimestamp } from '@data/datahub/emergency/types';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useAtomCallback } from 'jotai/utils';

type APIResponse = {
  emergencies: EmergencyTimestamp[];
  page_size: number;
  next_page_token?: string;
};

export const useEmergencyTimestampsLatestAPI = (): {
  loading: boolean;
  getEmergencyTimestampsLatestOnce: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<APIResponse | null>;
} => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/emergencies/latest?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              emergencies: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getEmergencyTimestampsLatestOnce = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_emergencies', { status: 'failed' }),
          });
          setLoading(false);
          return null;
        }

        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          params,
        );

        // 取得失敗時
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.datahub.get_emergencies', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return null;
        }

        return {
          ...res.value,
          emergencies: res.value.emergencies.filter(
            (data) =>
              !isNullOrUndefined(data.start_timestamp) &&
              data.start_timestamp !== '' &&
              !isNullOrUndefined(data.end_timestamp) &&
              data.end_timestamp !== '',
          ),
        };
      },
      [request, notifyError, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getEmergencyTimestampsLatestOnce,
  };
};

export const useEmergencyTimestampsAPI = (): {
  loading: boolean;
  emergencyTimestamps: EmergencyTimestamp[];
  getEmergencyTimestamps: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<void>;
  getEmergencyTimestampsAll: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<EmergencyTimestamp[]>;
} => {
  const [loading, setLoading] = useState(false);
  const [emergencyTimestamps, setEmergencyTimestamps] = useState<
    EmergencyTimestamp[]
  >([]);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/emergencies?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              emergencies: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getEmergencyTimestampsAll = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_emergencies', { status: 'failed' }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        let datas: EmergencyTimestamp[] = [];

        const splitRequest = async (): Promise<'success' | 'error'> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_emergencies', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return 'error';
          }
          // 取得成功時
          if (!!res && res.isSuccess()) {
            // 配列を結合
            datas = [
              ...datas,
              ...res.value.emergencies.filter(
                (data) =>
                  !isNullOrUndefined(data.start_timestamp) &&
                  data.start_timestamp !== '' &&
                  !isNullOrUndefined(data.end_timestamp) &&
                  data.end_timestamp !== '',
              ),
            ];
            if (res.value.next_page_token) {
              // レスポンスに next_page がある場合は次のリクエストを行う
              if (params) {
                params.set('page_token', res.value.next_page_token);
              }
              await splitRequest();
            }
          }
          return 'success';
        };

        setLoading(false);

        const res = await splitRequest();
        if (res === 'error') {
          return [];
        }

        return datas;
      },
      [getErrorMessage, t, request, notifyError],
    ),
  );

  const getEmergencyTimestamps = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        setEmergencyTimestamps([]);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_emergencies', { status: 'failed' }),
          });
          setLoading(false);
          return;
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (): Promise<void> => {
          const res = await req(params);
          // 取得失敗時
          if (!res.value || res.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_emergencies', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return;
          }
          // 取得成功時
          if (!!res && res.isSuccess()) {
            // 配列を結合
            setEmergencyTimestamps((prevState) =>
              prevState.concat(
                res.value.emergencies.filter(
                  (data) =>
                    !isNullOrUndefined(data.start_timestamp) &&
                    data.start_timestamp !== '' &&
                    !isNullOrUndefined(data.end_timestamp) &&
                    data.end_timestamp !== '',
                ),
              ),
            );
            if (res.value.next_page_token) {
              // レスポンスに next_page がある場合は次のリクエストを行う
              if (params) {
                params.set('page_token', res.value.next_page_token);
              }
              await splitRequest();
            } else {
              setLoading(false);
            }
          }
        };

        await splitRequest();

        setLoading(false);
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    loading,
    emergencyTimestamps,
    getEmergencyTimestamps,
    getEmergencyTimestampsAll,
  };
};
